import type { Locale } from '@apm/i18n';
import type {
  BloomDeviceRegisterRequest,
  BloomDeviceRegisterResponse,
  NativePushRegistration,
} from '@apm/contracts';
import type { PushMeans } from './push/subscription.js';

/**
 * BLOOM 기기 등록 (`docs/13` §1.4 · §3.1).
 *
 * ── 왜 TV2M 등록과 별개로 한 번 더 등록하는가 ─────────────────
 *
 * **같은 `deviceId` 가 두 서버에 등록된다** (`docs/12` §6 — "TV2M `device_id` 는 BLOOM Server 와
 * TV2M 양쪽에 같은 값으로"). 각자 다른 것을 안다.
 *
 * - TV2M `Device` — 가구 그래프. 이 기기가 어느 IP 에서 보이는가
 * - BLOOM Server `BloomDevice` — **계정과 구독.** 이 기기가 누구 것이고 어디로 보내는가
 *
 * TV2M 이 돌려준 `targetDeviceIds` 를 BLOOM Server 가 자기 구독으로 풀 수 있는 이유가 그
 * `id` 하나이고, 그 연결이 federated 발송의 전부다 — 어긋나면 매칭은 성공하는데
 * 보낼 곳이 없다 (`bloom-device.service.ts`).
 *
 * **Tier 1 의 입력도 여기서 만들어진다.** 로그인 상태로 등록해야 `BloomDevice.userId` 가
 * 채워지고, 그것이 없으면 같은 계정의 폰을 찾을 수 없다 (`docs/14` B6 의 1번).
 */
export async function registerBloomDevice(options: {
  serverUrl: string;
  /** 로그인 상태면 Bearer 로 붙인다. 없으면 익명 등록이고 계정에 묶이지 않는다 */
  token: string | null;
  deviceId: string;
  appId: string;
  /**
   * 셸 안이면 `ios` 다 (v12). **N2 까지 여기가 `'web'` 하드코딩이었고** 그 주석이
   * 「네이티브 포팅에서 이 값이 갈린다」로 예고해 둔 자리다 (`docs/29` §5-5).
   *
   * 서버가 이것으로 발송 수단을 고르지는 않는다 — 그것은 `nativePush` 의 유무가
   * 정한다(규칙 38). 기록이 틀리면 분석이 틀릴 뿐이지만, 틀린 채 두면 「iOS 사용자가
   * 0명인 대시보드」가 된다.
   */
  platform?: BloomDeviceRegisterRequest['platform'];
  /**
   * 알림을 보낼 수단. 없으면 기기만 등록된다 (v12, 규칙 38).
   *
   * **둘 중 하나만 실린다.** 서버는 둘이 함께 오면 400 이다 — 어느 쪽으로 보낼지가
   * 갈리지 않기 때문이고, 그 검사는 N4 가 세웠다.
   */
  means?: PushMeans;
  /**
   * 화면 언어 (v9, `docs/22` §8). **Push 알림 문구가 이 값으로 갈린다** — 서버는 브라우저의
   * 언어 설정을 모르고 Service Worker 도 앱의 저장소를 읽을 수 없다.
   */
  locale?: Locale;
}): Promise<BloomDeviceRegisterResponse> {
  const request: BloomDeviceRegisterRequest = {
    deviceId: options.deviceId,
    platform: options.platform ?? 'web',
    appId: options.appId,
    ...pushFields(options.means),
    ...(options.locale ? { locale: options.locale } : {}),
  };

  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (options.token !== null) headers['Authorization'] = `Bearer ${options.token}`;

  const response = await fetch(`${base(options.serverUrl)}/v1/bloom/device`, {
    method: 'POST',
    headers,
    body: JSON.stringify(request),
  });
  if (!response.ok) throw new Error(`기기 등록 실패 (HTTP ${response.status})`);
  return (await response.json()) as BloomDeviceRegisterResponse;
}

/**
 * 수단을 요청 본문의 필드로 옮긴다.
 *
 * **`forbidNonWhitelisted` 라 계약 밖 필드가 하나라도 있으면 요청 전체가 400 이다**
 * (v9 함정 ②). 그래서 없는 것은 키 자체를 싣지 않는다 — `undefined` 를 넣으면
 * `JSON.stringify` 가 빼 주지만, 그 성질에 기대는 것과 명시하는 것은 다르다.
 */
function pushFields(means?: PushMeans): Partial<BloomDeviceRegisterRequest> {
  if (means === undefined) return {};
  if (means.kind === 'native') return { nativePush: means.nativePush };
  return { pushSubscription: means.subscription, subscribedWithKey: means.subscribedWithKey };
}

/** 셸이 준 APNs 등록을 수단으로 감싼다. 호출부의 `{ kind: 'native' }` 반복을 없앤다 */
export function nativeMeans(nativePush: NativePushRegistration): PushMeans {
  return { kind: 'native', nativePush };
}

/**
 * 두 등록 응답이 **같은 사실을 말하는가.**
 *
 * 자가 치유가 heartbeat 마다 재등록하게 되면서 필요해졌다 (`useTv2m.ts` 의 그 자리).
 * 5분마다 새 객체로 `setState` 하면 아무것도 바뀌지 않은 날에도 시청 화면이 계속 다시
 * 그려진다 — 재생을 깨뜨리지는 않지만(`<video>` 는 그대로다) 값 없는 작업이고,
 * 같을 때 `prev` 를 **그대로** 돌려주면 React 가 렌더를 건너뛴다.
 *
 * `reason` 은 선택 필드라 「없다」와 「`undefined` 다」를 같게 본다 — 서버가 그 키를
 * 빼고 보내는 것과 명시적으로 비우는 것이 같은 뜻이기 때문이다.
 */
export function sameRegistration(
  a: BloomDeviceRegisterResponse | null,
  b: BloomDeviceRegisterResponse | null,
): boolean {
  if (a === null || b === null) return a === b;
  return (
    a.deviceId === b.deviceId &&
    a.linkedToAccount === b.linkedToAccount &&
    a.pushReady === b.pushReady &&
    (a.reason ?? null) === (b.reason ?? null)
  );
}

/**
 * BLOOM 쪽 구독만 지운다. **기기 행은 남는다** — 알림을 끈 것이지 기기가 사라진 것이
 * 아니다 (`bloom-device.service.ts` 와 같은 방침).
 *
 * 없는 기기를 지워도 성공이다(204). 취소는 멱등해야 한다.
 */
export async function unsubscribeBloomDevice(options: {
  serverUrl: string;
  deviceId: string;
}): Promise<void> {
  const response = await fetch(
    `${base(options.serverUrl)}/v1/bloom/device/${encodeURIComponent(options.deviceId)}/subscription`,
    { method: 'DELETE' },
  );
  if (!response.ok) throw new Error(`구독 해제 실패 (HTTP ${response.status})`);
}

/**
 * 로그아웃 — 이 기기를 계정에서 뗀다 (`docs/14` B8).
 *
 * **구독은 지우지 않는다.** 알림을 끈 것이 아니라 계정을 뗀 것이고, 이 기기는 여전히
 * Tier 2·4 의 대상이다 — 로그인하지 않은 기기도 가구로는 매칭된다 (`docs/15` §3-4).
 *
 * 토큰이 필요하다. 소유권을 확인할 수 있는 유일한 자리이고, 만료된 토큰으로는 뗄 수
 * 없다 — 그때는 앱이 이 호출을 건너뛰고 로컬 토큰만 버린다.
 */
export async function unlinkBloomDeviceAccount(options: {
  serverUrl: string;
  deviceId: string;
  token: string;
}): Promise<void> {
  const response = await fetch(
    `${base(options.serverUrl)}/v1/bloom/device/${encodeURIComponent(options.deviceId)}/account`,
    { method: 'DELETE', headers: { Authorization: `Bearer ${options.token}` } },
  );
  if (!response.ok) throw new Error(`계정 연결 해제 실패 (HTTP ${response.status})`);
}

function base(serverUrl: string): string {
  return serverUrl.replace(/\/+$/, '');
}
